import { useRef, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Box, Button, FileButton, Group, Modal, Stack, Text, UnstyledButton } from "@mantine/core";
import { useArtworkLibrary, useUploadArtwork } from "../api/admin";
import { ApiError } from "../api/client";
import { nativeRuntime } from "../platform/native";
import { Artwork } from "./Artwork";
import { pickNativeImage } from "./nativeImagePicker";
import { Chip } from "./Chip";
import { FeedbackLine } from "./FeedbackLine";

/** Matches settings.max_upload_bytes in config.py. The server is the real check; this only
 *  avoids spending an uplink on a file that is going to be refused. */
export const MAX_ARTWORK_BYTES = 8 * 1024 * 1024;

/** Why this upload will not be attempted, or null to go ahead. */
export function artworkRejection(bytes: number, t: TFunction): string | null {
  if (bytes > MAX_ARTWORK_BYTES) {
    return t("artworkPicker.tooBig", { kb: Math.round(bytes / 1024) });
  }
  return null;
}

/**
 * Choose a cover for one segment from the images people have uploaded.
 *
 * It only chooses: `onPick` hands the id (or null, to remove) back to the caller, which owns
 * the write. That keeps the same picker usable wherever a cover is set.
 */
export function ArtworkPicker({
  current,
  onPick,
  onClose,
}: {
  current: number | null;
  onPick: (artworkId: number | null) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const library = useArtworkLibrary();
  const upload = useUploadArtwork();
  const runtime = nativeRuntime();
  // The one rejection the server never sees, so the one this component has to word itself.
  const [tooBig, setTooBig] = useState<string | null>(null);
  const [pickError, setPickError] = useState<unknown>(null);
  // Re-picking the same file fires no `change` unless the input is cleared first.
  const resetFile = useRef<() => void>(null);

  const items = library.data ?? [];

  return (
    <Modal opened onClose={onClose} title={t("artworkPicker.heading")} size="lg">
      <Stack gap="sm">
        {runtime ? (
          <Button
            disabled={upload.isPending}
            onClick={() => {
              setPickError(null);
              void pickNativeImage(
                runtime,
                (bytes) => artworkRejection(bytes, t),
                (file) => upload.mutate(file, { onSuccess: (art) => onPick(art.id) }),
              )
                .then(setTooBig)
                .catch(setPickError);
            }}
          >
            {t("common.chooseFile")}
          </Button>
        ) : (
          <FileButton
            resetRef={resetFile}
            accept="image/png,image/jpeg,image/webp"
            onChange={(file) => {
              resetFile.current?.();
              setPickError(null);
              if (!file) return;
              const rejection = artworkRejection(file.size, t);
              setTooBig(rejection);
              if (rejection === null) {
                upload.mutate(file, { onSuccess: (art) => onPick(art.id) });
              }
            }}
          >
            {(props) => <Button {...props}>{t("common.chooseFile")}</Button>}
          </FileButton>
        )}
        <Text c="dimmed" size="sm">
          {t("artworkPicker.fileHint")}
        </Text>

        {items.length === 0 ? (
          <Text c="dimmed" size="sm">
            {t("artworkPicker.empty")}
          </Text>
        ) : (
          // Auto-fill so the panel width decides the columns.
          <Box
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
              gap: "0.5rem",
              maxHeight: "40vh",
              overflowY: "auto",
            }}
          >
            {items.map((art) => (
              <UnstyledButton
                key={art.id}
                type="button"
                aria-pressed={art.id === current}
                onClick={() => onPick(art.id)}
                style={{
                  borderRadius: "var(--mantine-radius-md)",
                  lineHeight: 0,
                  ...(art.id === current && {
                    outline: "2px solid var(--mantine-color-accent-filled)",
                    outlineOffset: 2,
                  }),
                }}
              >
                <Artwork src={art.url} seed={String(art.id)} size={96} alt="" />
              </UnstyledButton>
            ))}
          </Box>
        )}

        <FeedbackLine role="alert">
          {(tooBig || pickError || upload.error) &&
            (tooBig ??
              (pickError instanceof Error
                ? pickError.message
                : pickError
                  ? String(pickError)
                  : null) ??
              (upload.error instanceof ApiError ? upload.error.detail : String(upload.error)))}
        </FeedbackLine>

        <Group justify="flex-end" gap="xs">
          {current !== null && (
            <Chip variant="danger" onClick={() => onPick(null)}>
              {t("artworkPicker.removeArtwork")}
            </Chip>
          )}
          <Chip onClick={onClose}>{t("common.close")}</Chip>
        </Group>
      </Stack>
    </Modal>
  );
}
